import { useApolloClient, useMutation, gql } from '@apollo/client'
import { Formik } from 'formik'
import { difference, isEmpty } from 'lodash'
import { FormattedMessage, useIntl } from 'react-intl'
import styled from 'styled-components'

import { Button } from 'components/v2/Button'
import { Column } from '@10xLabs/react-web/components/Column'
import { ErrorMessage } from 'components/v2/ErrorMessage'
import { FormattedDate } from '@10xLabs/react-web/components/FormattedDate'
import { FormattedTimeByDate } from '@10xLabs/react-web/components/FormattedTimeByDate'
import Heading from 'components/v2/Heading'
import Modal from 'components/v2/Modal'
import { Row } from '@10xLabs/react-web/components/Row'
import { resolvers as tripsResolvers } from 'resolvers/trips'
import { getUnprocessableErrors } from 'utils/error'
import { isSuperAdmin } from 'features/Users/utils'
import { schema } from './schema'
import { useLogin } from 'features/Login/hooks'
import { MultiUserAutocomplete } from 'features/Users/components/MultiUserAutocomplete'
import { GTTripV2, GTUserSuggestion } from 'gql/types.generated'
import { ReactElement } from 'react'
import { EUserType } from 'features/Users/types'

const ModalContainer = styled(Modal.Container)`
  max-width: 600px;
`

const DateWrapper = styled.div<TSFixMe>`
  display: flex;
  margin: 3px 0;
`

const CHANGE_TRIP_CREW_USERS = gql`
  mutation ChangeTripUsers($input: ChangeTripUsersInput!) {
    changeTripUsers(input: $input)
  }
`

const CHANGE_LOCAL_TRIP_CREW_USERS = gql`
  mutation ChangeLocalTripCrewUsers($tripId: ID!, $crewUsers: CrewUsers!) {
    changeTripCrewUsers(tripId: $tripId, crewUsers: $crewUsers) @client
  }
`

interface IChangeCrewUsersModalProps {
  trip: GTTripV2
  closeModal: () => void
}

export const ChangeCrewUsersModal = ({ trip, closeModal }: IChangeCrewUsersModalProps): ReactElement => {
  const intl = useIntl()
  const client = useApolloClient()
  client.addResolvers(tripsResolvers)
  const login = useLogin()

  const [changeLocalTripCrewUsers] = useMutation(CHANGE_LOCAL_TRIP_CREW_USERS)
  const [changeTripVehicle, { loading, error }] = useMutation(CHANGE_TRIP_CREW_USERS)

  const handleChangeCrewUsers = async (values: TSFixMe): Promise<TSFixMe> => {
    const oldCrewUserIds = trip.crewUsers.map(({ id }: TSFixMe): TSFixMe => id)
    const newCrewUserIds = values.crewUsers.map(({ id }: TSFixMe): TSFixMe => id)
    const hasDifferentUsers = !(
      isEmpty(difference(newCrewUserIds, oldCrewUserIds)) && isEmpty(difference(oldCrewUserIds, newCrewUserIds))
    )

    if (hasDifferentUsers) {
      try {
        await changeTripVehicle({
          variables: {
            input: {
              id: trip.id,
              userIDs: values.crewUsers.map(({ id }: TSFixMe): TSFixMe => id)
            }
          }
        })
        changeLocalTripCrewUsers({
          variables: {
            tripId: trip.id,
            crewUsers: values.crewUsers
          }
        })
        closeModal()
      } catch (ex) {
        // Do nothing
      }
    } else {
      closeModal()
    }
  }

  const initialCrewUsers = {
    crewUsers: trip.crewUsers || []
  }

  const firstStop = trip.itinerary?.at(0)
  const lastStop = trip.itinerary?.at(-1)

  return (
    <ModalContainer large>
      <Modal.Header>
        <Heading.H5>
          <FormattedMessage
            id="trips.changeCrewUsers.title"
            defaultMessage="Change crew users for trip {id}"
            values={{
              id: trip.id.substring(0, 8)
            }}
          />
        </Heading.H5>
      </Modal.Header>
      {/* @ts-expect-error TS(2559) FIXME Remove this comment to see the full error message*/}
      <Formik initialValues={initialCrewUsers} onSubmit={handleChangeCrewUsers} validationSchema={schema(intl)}>
        {({ setFieldValue, values, handleSubmit, dirty }): ReactElement => {
          return (
            <form onSubmit={handleSubmit}>
              <Modal.Content>
                <Row>
                  <Column>
                    <div>
                      <FormattedMessage
                        id="trip.changeCrewUsers.confirmation.tripDetails"
                        defaultMessage="Trip from <strong>{origin}</strong> to <strong>{destination}</strong>"
                        values={{
                          origin: firstStop?.place.name,
                          destination: lastStop?.place.name,
                          strong: (msg) => <strong>{msg}</strong>
                        }}
                      />
                    </div>
                    <DateWrapper>
                      <FormattedDate
                        intl={intl}
                        value={trip.departureAt}
                        month="2-digit"
                        day="2-digit"
                        year="numeric"
                      />
                      &nbsp;
                      <FormattedTimeByDate
                        intl={intl}
                        date={trip.departureAt}
                        timeZone={firstStop?.place.timezone || undefined}
                      />
                    </DateWrapper>
                  </Column>
                </Row>
                <Row>
                  <Column>
                    <MultiUserAutocomplete
                      id="crewUsers"
                      type={EUserType.Crew}
                      // @ts-expect-error FIXME
                      selectedUsers={values.crewUsers}
                      onChange={(newUsers: GTUserSuggestion[]): void => {
                        setFieldValue('crewUsers', newUsers)
                      }}
                      companyId={isSuperAdmin(login.accessToken.conditions) ? null : trip.company.id}
                      label={<FormattedMessage id="crewUsers.autocomplete.label" defaultMessage="Crew users" />}
                    />
                  </Column>
                </Row>
              </Modal.Content>
              <Modal.Footer>
                {error && (
                  <Modal.ErrorMessage>
                    <ErrorMessage errors={getUnprocessableErrors(error)}>
                      <FormattedMessage
                        id="trips.changeCrewUsers.error"
                        defaultMessage="There was an error while changing the crew users"
                      />
                    </ErrorMessage>
                  </Modal.ErrorMessage>
                )}
                <Modal.Actions>
                  <Button type="button" kind="cancel" onClick={(): void => closeModal()}>
                    <FormattedMessage id="dictionary.close" defaultMessage="Close" />
                  </Button>
                  <Button type="submit" kind="primary" loading={loading} disabled={!dirty}>
                    <FormattedMessage id="trips.changeCrewUsers.action" defaultMessage="Change crew users" />
                  </Button>
                </Modal.Actions>
              </Modal.Footer>
            </form>
          )
        }}
      </Formik>
    </ModalContainer>
  )
}
